import { Link } from "react-router-dom";
import { MapPin, PawPrint, KeyRound, Check, Plus, Star, Phone } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { useCompare } from "@/hooks/useCompare";
import { communityUrl, img, money, RENTAL_LABEL } from "@/lib/api";

const Chip = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-[#334155]">
    <Icon className="h-3.5 w-3.5 text-[#027A6D]" aria-hidden /> {children}
  </span>
);

export const CommunityCard = ({ c, compact = false }) => {
  const { has, toggle } = useCompare();
  const inCompare = has(c.id);
  const pets = c.pets_allowed == null ? null : c.pets_allowed ? "Pets OK" : "No pets";
  const rental = RENTAL_LABEL[c.rental_policy];
  const hasFee = c.fee_range || c.monthly_fee != null;
  return (
    <article data-testid={`community-card-${c.id}`} className="group relative rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300">
      <Link to={communityUrl(c)} className="block" data-testid={`community-card-link-${c.id}`}>
        <div className={`relative ${compact ? "h-32 sm:h-36" : "h-40 sm:h-44"} overflow-hidden`}>
          <img src={img(c.image)} alt={`Neighborhood scene representing ${c.name}`} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute top-3 left-3"><StatusBadge status={c.status} /></div>
          {hasFee && (
            <div className="absolute bottom-3 left-3 rounded-lg bg-white/95 backdrop-blur px-3 py-1.5 shadow">
              {c.fee_estimated || !c.fee_range?.includes(" - ") ? (
                <span className="text-sm text-slate-600" data-testid={`card-fee-${c.id}`}>From <span className="font-display text-lg font-bold text-[#0B2150]">{money(c.monthly_fee)}</span>/month</span>
              ) : (
                <>
                  <span className="font-display text-lg font-bold text-[#0B2150]">{c.fee_range || money(c.monthly_fee)}</span>
                  {!c.fee_range && <span className="text-xs text-slate-600">/mo</span>}
                </>
              )}
            </div>
          )}
        </div>
        <div className="p-4 space-y-2">
          <h3 className="font-semibold text-[#0B2150] leading-snug line-clamp-1">{c.name}</h3>
          <p className="flex items-center gap-1 text-sm text-slate-600"><MapPin className="h-3.5 w-3.5" aria-hidden />{c.city}, {c.state_code}</p>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {pets && <Chip icon={PawPrint}>{pets}</Chip>}
            {rental && <Chip icon={KeyRound}>{rental}</Chip>}
            {c.rating != null && <Chip icon={Star}>{c.rating} ({c.reviews} reviews)</Chip>}
          </div>
        </div>
      </Link>
      {c.phone && (
        <div className="px-4 pb-4 -mt-2">
          <a href={`tel:${c.phone.replace(/[^\d+]/g, "")}`} data-testid={`card-phone-link-${c.id}`} aria-label={`Call ${c.name} at ${c.phone}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#03A896] px-3 py-1.5 text-xs font-semibold text-[#027A6D] hover:bg-[#03A896] hover:text-white transition-colors">
            <Phone className="h-3.5 w-3.5" aria-hidden />{c.phone.replace(/^\+1 /, "")}
          </a>
        </div>
      )}
      <button onClick={() => toggle(c)} aria-pressed={inCompare} data-testid={`compare-add-button-${c.id}`}
        aria-label={inCompare ? `Remove ${c.name} from compare` : `Add ${c.name} to compare`}
        className={`absolute top-3 right-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold shadow transition-colors ${inCompare ? "bg-[#03A896] text-white" : "bg-white/95 text-[#0B2150] hover:bg-[#0B2150] hover:text-white"}`}>
        {inCompare ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} Compare
      </button>
    </article>
  );
};
